<template>
  <q-page v-if="dataService" class="main-wrapper-page single-page" style="background-color: var(--q-accent)">
    <div class="row no-wrap">
      <div class="col col-2">
        <div class="left-sidebar">
          <infra-service-logo
            v-if="dataService.icon && dataService.icon !== 'default'"
            :logo="dataService.icon"
            style="width: 120px"
          />
          <span v-else class="icon-service q-mb-lg">
            <q-icon name="settings" color="white" size="80px"></q-icon>
          </span>
        </div>
        <ul class="section-titles q-px-none q-py-none q-mx-none q-my-none">
          <li @click="scrollToItem('generalInfo')">Info</li>
          <li @click="scrollToItem('onboardedAPMs')">Onboarded APMs</li>
          <li @click="scrollToItem('adrs')">ADRs</li>
          <li @click="scrollToItem('typ')">Technical Yellow Page</li>
        </ul>
      </div>
      <div class="col col-7 q-px-md">
        <q-scroll-area
          style="width: 100%; padding-right: 10px"
          :style="`height: calc(100vh - ${currentBanner ? '175px' : '120px'})`"
          :thumb-style="{ borderRadius: '5px', background: '#9f9f9f', width: '3px', opacity: 1, right: 0 }"
        >
          <div class="service-section q-mb-md q-px-sm q-py-lg bg-white" ref="generalInfo">
            <q-expansion-item
              expand-separator
              header-style="font-size: 24px; font-weight: bold;"
              default-opened
              dense
              header-class="q-mt-none q-mb-none button-no-hover"
            >
              <template v-slot:header>
                <q-item-section class="descr-header"
                  >General Info
                  <q-btn v-if="canBeEdited" @click.stop="showNewDescriptionDialog()" icon="add" flat round size="14px">
                    <q-tooltip>Add description</q-tooltip>
                  </q-btn>
                </q-item-section>
              </template>
              <q-card>
                <q-card-section>
                  <q-expansion-item
                    v-for="(item, key) in dataService.descriptions"
                    expand-separator
                    :default-opened="key === 0"
                    group="descriptions"
                    header-style="font-size: 20px; font-weight: 500;"
                    :key="item.id"
                    dense
                    class="q-pt-xs q-pb-xs"
                    header-class="q-mt-none q-mb-none button-no-hover"
                  >
                    <template v-slot:header>
                      <q-item-section class="descr-header"
                        >{{ item.type }}
                        <q-btn
                          v-if="canBeEdited && !requiredDescriptions.includes(item.type)"
                          @click.stop="removeDescription(item.id)"
                          icon="delete"
                          class="q-ml-auto"
                          flat
                          round
                          size="12px"
                        >
                          <q-tooltip>Remove description</q-tooltip>
                        </q-btn>
                        <q-btn
                          v-if="canBeEdited"
                          @click.stop="showEditDescriptionDialog(item.id)"
                          icon="edit"
                          class="q-mr-md"
                          flat
                          round
                          size="12px"
                        >
                          <q-tooltip>Edit description</q-tooltip>
                        </q-btn>
                      </q-item-section>
                    </template>
                    <q-card>
                      <q-card-section>
                        <div
                          v-if="item.text && item.text.trim().length"
                          class="single-page-markdown"
                          v-html="dataMarkdown(item.text)"
                        ></div>
                        <span v-else>Not yet provided</span>
                      </q-card-section>
                    </q-card>
                  </q-expansion-item>
                </q-card-section>
              </q-card>
            </q-expansion-item>
          </div>
          <div
            v-if="dataService?.pageSettings?.adrs || canBeEdited"
            class="service-section q-px-sm q-py-lg bg-white q-mb-md"
            ref="adrs"
          >
            <q-expansion-item
              expand-separator
              header-style="font-size: 24px; font-weight: bold;"
              dense
              header-class="q-mt-none q-mb-none button-no-hover"
            >
              <template v-slot:header>
                <q-item-section class="descr-header"
                  >ADRs
                  <q-btn v-if="canBeEdited" @click.stop="showEditAdrs = true" icon="edit" flat round size="14px">
                    <q-tooltip>Edit ADRs</q-tooltip>
                  </q-btn>
                </q-item-section>
              </template>
              <q-card>
                <q-card-section>
                  <div v-if="dataService?.adrs?.length" class="row q-col-gutter-xs adrs-holder">
                    <div
                      v-for="adr in dataService.adrs"
                      :key="adr.id"
                      @click="goToAdr($event, adr.adrId)"
                      class="col-6 q-px-xs q-py-xs adr-holder"
                    >
                      <div class="adr">
                        <span class="adr-title">{{ adr.name }}</span>
                        <span class="scope" style="color: #009688; font-size: 14px"
                          >For {{ adr.scope?.units }}, {{ adr.scope?.regions }}</span
                        >
                        <div
                          class="adr-content service-description-markdown"
                          v-html="dataMarkdown(adr.requirements || adr.content)"
                        ></div>
                        <span class="block"><b>Valid date:</b> {{ adr.validDate }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-content flex column items-center">
                    <q-icon name="do_disturb_alt" size="90px" color="primary"></q-icon>
                    No data
                  </div>
                </q-card-section>
              </q-card>
            </q-expansion-item>
          </div>
          <div
            v-if="dataService?.pageSettings?.onboardedApms || canBeEdited"
            class="service-section q-px-sm q-mb-md q-py-lg bg-white"
            ref="onboardedAPMs"
          >
            <q-expansion-item
              expand-separator
              header-style="font-size: 24px; font-weight: bold;"
              dense
              header-class="q-mt-none q-mb-none button-no-hover"
            >
              <template v-slot:header>
                <div class="item-header descr-header full-width">
                  <span>{{ dataService?.registrations.length }} Onboarded APMs</span>

                  <q-btn @click.stop="downloadCsv()" icon="download" round flat class="q-ml-xs">
                    <q-tooltip>Download as csv</q-tooltip>
                  </q-btn>
                  <q-btn
                    v-if="canBeEdited"
                    class="q-ml-auto"
                    @click.stop="showEditOnboardApms = true"
                    icon="edit"
                    flat
                    round
                    size="14px"
                  >
                    <q-tooltip>Edit Onboarded APMs</q-tooltip>
                  </q-btn>
                </div>
              </template>
              <q-card>
                <q-card-section>
                  <div class="row no-wrap full-width">
                    <div class="col q-mr-xs">
                      <q-input v-model="onboardedAPMsFilters.apmId" label="APMID" outlined dense clearable></q-input>
                    </div>
                    <div class="col q-mr-xs">
                      <q-input v-model="onboardedAPMsFilters.name" label="Name" outlined dense clearable></q-input>
                    </div>
                    <div class="col">
                      <q-select
                        v-model="onboardedAPMsFilters.businessDomain"
                        use-input
                        input-debounce="0"
                        label="Business domain"
                        :options="businessDomainOptions"
                        popup-content-class="alternative-id-popup"
                        @filter="filterFn"
                        clearable
                        options-dense
                        outlined
                        dense
                      >
                        <template v-slot:selected>
                          <span class="business-domain-value">{{ onboardedAPMsFilters.businessDomain }}</span>
                        </template>
                      </q-select>
                    </div>
                  </div>
                </q-card-section>
                <q-card-section>
                  <div v-if="showingDs?.length" class="row q-col-gutter-xs">
                    <router-link
                      :to="`/my-portfolio/${apm.apmId}`"
                      v-for="(apm, key) in showingDs"
                      :key="key"
                      class="col-6 q-px-xs q-py-xs apm-link"
                    >
                      {{ apm.name }}
                      <sup>{{ apm.apmId }}</sup>
                    </router-link>
                    <div class="flex flex-center full-width q-mt-md">
                      <q-pagination
                        :max="dsPages"
                        boundary-numbers
                        :max-pages="4"
                        direction-links
                        v-model="dsPage"
                      ></q-pagination>
                    </div>
                  </div>
                  <div v-else class="no-content flex column items-center">
                    <q-icon name="do_disturb_alt" size="90px" color="primary"></q-icon>
                    No data
                  </div>
                </q-card-section>
              </q-card>
            </q-expansion-item>
          </div>
          <div
            v-if="
              this.dataService.apmid &&
              this.dataService.apmid.trim() !== '' &&
              this.dataService.apmid.toLowerCase() !== 'n/a' &&
              (dataService?.pageSettings?.typ || canBeEdited)
            "
            class="service-section q-px-sm q-py-lg bg-white"
            ref="typ"
          >
            <q-expansion-item
              expand-separator
              header-style="font-size: 24px; font-weight: bold;"
              dense
              header-class="q-mt-none q-mb-none button-no-hover"
            >
              <template v-slot:header>
                <q-item-section class="descr-header"
                  >{{ dataService?.apmid }} - Technical Yellow Page
                  <q-btn v-if="canBeEdited" @click.stop="showEditTyp = true" icon="edit" flat round size="14px">
                    <q-tooltip>Edit Technical Yellow Page</q-tooltip>
                  </q-btn>
                </q-item-section>
              </template>
              <q-card>
                <q-card-section>
                  <TechnicalYellowPage
                    @goToOnboardedServices="scrollToItem('onboardedAPMs')"
                    @goToAdrs="scrollToItem('adrs')"
                    :dataService="dataService"
                    :apmDetails="apmDetails"
                  />
                </q-card-section>
              </q-card>
            </q-expansion-item>
          </div>
        </q-scroll-area>
      </div>
      <div class="col col-3">
        <q-scroll-area
          style="width: 100%; padding-right: 10px"
          :style="`height: calc(100vh - ${currentBanner ? '175px' : '120px'})`"
          :thumb-style="{ borderRadius: '5px', background: '#9f9f9f', width: '3px', opacity: 1, right: 0 }"
        >
          <q-btn
            v-if="dataService && dataService.orderable"
            class="service-btn q-mx-none q-py-md q-mb-md"
            unelevated
            :disable="
              !!addServiceError?.length ||
              notCanBeDeleted ||
              uiConfig.disableEditServices.includes(dataService?.apmid) ||
              uiConfig.hideServices.includes(dataService?.apmid) ||
              !currentService
            "
            color="primary"
            @click="addToServiceDesign"
            @mouseover="showRemove = true"
            @mouseout="showRemove = false"
          >
            <q-spinner v-if="addserviceLoad" color="grey-7" :thickness="3" size="34px" class="q-mb-sm" />
            <q-icon
              v-else
              :name="
                notCanBeDeleted ? 'lock' : isInDesign && showRemove ? 'remove' : isInDesign ? 'done' : 'control_point'
              "
              color="white"
              size="lg"
            ></q-icon>
            {{
              notCanBeDeleted
                ? 'Can not be removed'
                : isInDesign && showRemove
                ? 'Remove'
                : isInDesign
                ? 'Added to my Digital Service'
                : 'Add to my digital service'
            }}
            <q-tooltip v-if="!currentService">Please choose digital service</q-tooltip>
            <q-tooltip v-else-if="addServiceError?.length">{{ addServiceError }}</q-tooltip>
          </q-btn>
          <div :class="['service-section q-mb-md q-px-lg', lifecycleBgClass]">
            <div class="flex items-center justify-center text-center">
              <span
                v-if="dataService.state"
                style="width: 100%; color: white; text-transform: uppercase; font-weight: bold"
                class="q-my-md"
              >
                <q-btn v-if="canBeEdited" @click="showEditLifecycleDialog" flat
                  >Is {{ dataService.state }}
                  <q-tooltip>Set lifecycle stage</q-tooltip>
                </q-btn>
                <span v-else>Is {{ dataService.state }} </span>
              </span>
              <span v-else class="no-content flex column items-center">
                <q-icon name="do_disturb_alt" size="90px" color="primary"></q-icon>
                Temporary Unavailable
              </span>
            </div>
          </div>
          <div
            v-if="dataService?.pageSettings?.contacts || canBeEdited"
            class="service-section q-mb-md q-px-md q-py-md bg-white"
          >
            <div class="flex items-center justify-between">
              <h5 class="q-mt-none q-mb-sm">Contacts</h5>
              <q-btn v-if="canBeEdited" @click="showEditContactsDialog" icon="edit" flat round size="12px">
                <q-tooltip>Edit contacts section</q-tooltip>
              </q-btn>
            </div>
            <ul
              v-if="dataService.contacts && dataService.contacts.length"
              class="links q-px-none q-py-none q-mx-none q-my-none"
            >
              <li v-for="(contact, i) in dataService.contacts" :key="i" class="q-mb-sm">
                <span style="text-transform: capitalize" class="contact-label">{{ contact.label }}</span>
                <span style="text-transform: none">{{ contact.contact }}</span>
              </li>
            </ul>
            <div v-else class="no-content flex column items-center">
              <q-icon name="do_disturb_alt" size="90px" color="primary"></q-icon>
              Temporary Unavailable
            </div>
          </div>
          <div
            v-if="dataService?.pageSettings?.metadata || canBeEdited"
            class="service-section q-mb-md q-px-md q-py-md bg-white"
          >
            <div class="flex items-center justify-between">
              <h5 class="q-mt-none q-mb-sm">Metadata</h5>
              <q-btn v-if="canBeEdited" @click="showEditMetadataDialog" icon="edit" flat round size="12px">
                <q-tooltip>Edit metadata section</q-tooltip>
              </q-btn>
            </div>
            <ul
              v-if="dataService.metadata && Object.keys(dataService.metadata).length"
              class="links q-px-none q-py-none q-mx-none q-my-none"
            >
              <li v-for="(value, key) in dataService.metadata" :key="key" class="q-mb-sm">
                <span style="text-transform: capitalize" class="contact-label">{{ key }}</span>
                <span style="text-transform: none">{{ value }}</span>
              </li>
            </ul>
            <div v-else class="no-content flex column items-center">
              <q-icon name="do_disturb_alt" size="90px" color="primary"></q-icon>
              Temporary Unavailable
            </div>
          </div>
          <div class="service-section q-mb-md q-px-md q-py-md bg-white">
            <div class="flex items-center justify-between">
              <h5 class="q-mt-none q-mb-sm">Links</h5>
              <q-btn v-if="canBeEdited" @click="showEditLinksDialog" icon="edit" flat round size="12px">
                <q-tooltip>Edit links section</q-tooltip>
              </q-btn>
            </div>
            <ul
              v-if="dataService.links && dataService.links.length"
              class="links q-px-none q-py-none q-mx-none q-my-none"
            >
              <template v-for="link in dataService.links" :key="link.id">
                <li v-if="link.url" class="q-mb-sm">
                  <a :href="ensureProtocol(link.url)" target="_blank">
                    <q-icon name="mdi-link-variant" class="q-mr-xs" size="xs" color="primary"></q-icon>
                    {{ link.label || link.url }}
                  </a>
                </li>
              </template>
            </ul>
            <div v-if="dataService.standards && dataService.standards.length">
              <span
                class="block text-uppercase q-mb-sm q-mt-md"
                style="font-size: 10px; font-weight: bold; color: #979ba4; letter-spacing: 1px"
                >Relevant Standards</span
              >
              <ul class="links q-px-none q-py-none q-mx-none q-my-none">
                <li v-for="standard in dataService.standards" :key="standard.id" class="q-mb-sm">
                  <a :href="ensureProtocol(standard.standardLink)" target="_blank">
                    <q-icon name="engp:engp-ico-standards" class="q-mr-xs" size="16px" color="primary"></q-icon>
                    {{ standard.standardName }}
                  </a>
                </li>
              </ul>
            </div>
            <div
              v-if="
                !(
                  (dataService.links && dataService.links.length) ||
                  (dataService.standards && dataService.standards.length)
                )
              "
              class="no-content flex column items-center"
            >
              <q-icon name="do_disturb_alt" size="90px" color="primary"></q-icon>
              Temporary Unavailable
            </div>
          </div>
          <div
            v-if="dataService?.pageSettings?.details || canBeEdited"
            class="service-section q-mb-md q-px-md q-py-md bg-white"
          >
            <div class="flex items-center justify-between">
              <h5 class="q-mt-none q-mb-sm">Details</h5>
              <q-btn v-if="canBeEdited" @click="showEditDetailsDialog" icon="edit" flat round size="12px">
                <q-tooltip>Edit section</q-tooltip>
              </q-btn>
            </div>
            <!-- Details content -->

            <template v-for="(item, index) in details" :key="index">
              <div v-if="item.show" class="detail-item">
                <span
                  class="block text-uppercase q-mt-sm"
                  style="font-size: 10px; font-weight: bold; color: #979ba4; letter-spacing: 1px"
                  >{{ item.label }}</span
                >
                <span>{{ item.value }}</span>
                <q-avatar
                  v-if="item.avatar"
                  :color="supportHoursColor"
                  size="xs"
                  class="custom-avatar q-ml-xs q-mb-xs"
                ></q-avatar>
              </div>
            </template>
          </div>
          <div
            v-if="dataService.openHouseInfo.inviteLink !== 'N/A'"
            class="service-section q-mb-md q-px-md q-py-md bg-white"
          >
            <div class="flex items-center justify-between">
              <h5 class="q-mt-none q-mb-sm">Open House Info</h5>
              <q-btn v-if="canBeEdited" @click="showEditDetailsDialog" icon="edit" flat round size="12px">
                <q-tooltip>Edit section</q-tooltip>
              </q-btn>
            </div>
            <ul class="open-house full-width q-px-none q-py-none q-mx-none q-my-none">
              <li><b>Person: </b>{{ dataService.openHouseInfo.contactPerson }}</li>
              <li><b>Frequency: </b>{{ frequencyLabel }}</li>
              <li><a :href="ensureProtocol(dataService.openHouseInfo.inviteLink)" target="_blank">Invite link</a></li>
              <li>
                <a
                  v-if="dataService.openHouseInfo.wikiLink && dataService.openHouseInfo.wikiLink !== 'N/A'"
                  :href="ensureProtocol(dataService.openHouseInfo.wikiLink)"
                  target="_blank"
                  >Wiki</a
                >
                <span v-else>No wiki link</span>
              </li>
            </ul>
          </div>
          <q-btn
            class="service-btn support-btn q-mx-none q-py-md"
            unelevated
            outline
            flat
            text-color="text-color"
            @click="showFeedbackDialog"
          >
            <q-icon name="chat_bubble_outline" color="text-color" size="lg"></q-icon>
            Help us improve
          </q-btn>
        </q-scroll-area>
      </div>
    </div>
  </q-page>
  <FeedbackDialog
    @sendFeedback="createFeedback($event)"
    :feedbacks="dataService?.feedbacks || []"
    :show="feedbackDialog"
    :loading="loadFeedback"
    :service-name="dataService?.serviceName || dataService?.displayName || dataService?.apmid"
    @close="feedbackDialog = false"
  ></FeedbackDialog>
  <DialogForm
    :show="showEditLifecycle"
    :updLoading="updLoading"
    @close="showEditLifecycle = false"
    @submit="updateInfraservice"
    title="Edit lifecycle stage"
  >
    <q-select
      v-model="lifecycleStage"
      option-value="value"
      :rules="[(val) => !!val || 'Field is required']"
      map-options
      option-label="label"
      popup-content-class="alternative-id-popup"
      dense
      bg-color="white"
      options-dense
      outlined
      label="Lifecycle stage*"
      emit-value
      :options="stateOptions"
    />
  </DialogForm>
  <DialogForm
    :updLoading="updLoading"
    :show="showEditOnboardApms"
    @close="closeEditDialog"
    @submit="updateInfraservice"
    title="Edit Onboarded APMs"
  >
    <div>
      <q-checkbox v-model="pageSettings.onboardedApms" label="Show Onboarded APMs section"></q-checkbox>
    </div>
  </DialogForm>
  <DialogForm
    :updLoading="updLoading"
    :show="showEditTyp"
    @close="closeEditDialog"
    @submit="updateInfraservice"
    title="Edit Technical Yellow Page"
  >
    <div>
      <q-checkbox v-model="pageSettings.typ" label="Show Technical Yellow Page section"></q-checkbox>
    </div>
  </DialogForm>
  <DialogForm
    :updLoading="updLoading"
    :show="showEditAdrs"
    @close="closeEditDialog"
    @submit="updateInfraservice"
    title="Edit ADRs"
  >
    <div><q-checkbox v-model="pageSettings.adrs" label="Show ADRs section"></q-checkbox></div>
  </DialogForm>
  <DialogForm
    :show="showEditLinks"
    :updLoading="updLoading"
    @close="closeEditLinksDialog"
    @submit="updateInfraservice"
    title="Edit links"
  >
    <div class="row q-col-gutter-sm q-mb-sm" v-for="(link, key) in links" :key="key">
      <div class="col-4">
        <q-input
          v-model="links[key].label"
          :rules="[(val) => !!val || 'Field is required', (val) => val.length < 40 || 'Max length is 40 symbols']"
          outlined
          dense
          :disable="requiredLinks.includes(link.label) && links.filter((l) => l.label === link.label).length === 1"
          label="Label"
        ></q-input>
      </div>
      <div class="col-3">
        <q-select
          v-model="links[key].type"
          :options="linkTypes"
          popup-content-class="alternative-id-popup"
          outlined
          emit-value
          multiple
          map-options
          dense
          label="Type"
        ></q-select>
      </div>
      <div class="col-4">
        <q-input v-model="links[key].url" :rules="[urlRule]" outlined dense label="URL"></q-input>
      </div>
      <div class="col-1 text-center">
        <q-btn
          :disable="requiredLinks.includes(link.label) && links.filter((l) => l.label === link.label).length === 1"
          flat
          icon="remove"
          color="white"
          text-color="black"
          small
          class="q-px-none q-mt-sm btn--no-hover"
          :ripple="false"
          size="11px"
          @click="removeLink(key)"
        >
        </q-btn>
      </div>
    </div>
    <q-btn
      flat
      icon="add"
      color="white"
      text-color="black"
      small
      class="q-px-none btn--no-hover"
      :ripple="false"
      size="11px"
      label="Add link"
      @click="addLink"
    >
    </q-btn>
  </DialogForm>
  <DialogForm
    :updLoading="updLoading"
    :show="showEditContacts"
    @close="showEditContacts = false"
    @submit="updateInfraservice"
    title="Edit contacts"
  >
    <div class="row q-col-gutter-sm q-mb-sm" v-for="(contact, key) in contacts" :key="key">
      <div class="col-5">
        <q-input
          v-model="contacts[key].label"
          :rules="[(val) => !!val || 'Field is required', (val) => val.length < 40 || 'Max length is 40 symbols']"
          outlined
          dense
          label="Position"
        ></q-input>
      </div>
      <div class="col-6">
        <q-input v-model="contacts[key].contact" :rules="emailRules" dense outlined label="Email"></q-input>
      </div>
      <div class="col-1 text-center">
        <q-btn
          flat
          icon="remove"
          color="white"
          text-color="black"
          small
          class="q-px-none q-mt-sm btn--no-hover"
          :ripple="false"
          size="11px"
          @click="removeContact(key)"
        >
        </q-btn>
      </div>
    </div>
    <q-btn
      flat
      icon="add"
      color="white"
      text-color="black"
      small
      class="q-px-none btn--no-hover"
      :ripple="false"
      size="11px"
      label="Add contact"
      @click="addContact"
    >
    </q-btn>
    <div class="q-mt-lg"><q-checkbox v-model="pageSettings.contacts" label="Show Contacts section"></q-checkbox></div>
  </DialogForm>
  <DialogForm
    :updLoading="updLoading"
    :show="showEditMetadata"
    @close="showEditMetadata = false"
    @submit="updateInfraservice"
    title="Edit metadata"
  >
    <div class="row q-col-gutter-sm q-mb-sm" v-for="(entry, key) in metadata" :key="key">
      <div class="col-5">
        <q-input
          v-model="metadata[key].label"
          :rules="[(val) => !!val || 'Field is required', (val) => val.length < 40 || 'Max length is 40 symbols']"
          outlined
          dense
          label="Label"
        ></q-input>
      </div>
      <div class="col-6">
        <q-input
          v-model="metadata[key].value"
          :rules="[(val) => !!val || 'Field is required']"
          outlined
          dense
          label="Value"
        ></q-input>
      </div>
      <div class="col-1 text-center">
        <q-btn
          flat
          icon="remove"
          color="white"
          text-color="black"
          small
          class="q-px-none q-mt-sm btn--no-hover"
          :ripple="false"
          size="11px"
          @click="removeMetadata(key)"
        >
        </q-btn>
      </div>
    </div>
    <q-btn
      flat
      icon="add"
      color="white"
      text-color="black"
      small
      class="q-px-none btn--no-hover"
      :ripple="false"
      size="11px"
      label="Add metadata"
      @click="addMetadata"
    >
    </q-btn>
  </DialogForm>
  <DialogForm
    :show="showEditName"
    :updLoading="updLoading"
    @close="closeEditDialog"
    @submit="updateInfraservice"
    title="Edit"
  >
    <q-input
      v-model="displayName"
      :rules="[(val) => !!val || 'Field is required', (val) => val.length < 100 || 'Max length is 100 symbols']"
      outlined
      class="q-mb-sm"
      disable
      dense
      hint="If you would like to change display name, please contact our team."
      label="Display name"
    ></q-input>

    <q-input
      v-model="subtitle"
      :rules="[(val) => !!val || 'Field is required', (val) => val.length < 100 || 'Max length is 100 symbols']"
      outlined
      dense
      label="Subtitle"
    ></q-input>
    <div>
      <q-checkbox
        class="full-width"
        v-model="pageSettings.onboardedApms"
        label="Show Onboarded APMs section"
      ></q-checkbox>
      <q-checkbox class="full-width" v-model="pageSettings.contacts" label="Show Contacts section"></q-checkbox>
      <q-checkbox class="full-width" v-model="pageSettings.details" label="Show Details section"></q-checkbox>
      <q-checkbox class="full-width" v-model="pageSettings.typ" label="Show Technical yellow page section"></q-checkbox>
      <q-checkbox class="full-width" v-model="pageSettings.adrs" label="Show ADRs section"></q-checkbox>
      <q-checkbox class="full-width" v-model="pageSettings.metadata" label="Show Metadata section"></q-checkbox>
    </div>
  </DialogForm>

  <DialogForm
    :show="showEditDetails"
    :updLoading="updLoading"
    @close="showEditDetails = false"
    @submit="updateInfraservice"
    title="Edit details"
  >
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <span class="title" style="font-size: 20px">Open House Info</span>
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <q-item class="q-mr-auto">
          <q-item-section>
            <q-item-label class="text-right">Contact Person</q-item-label>
          </q-item-section>
        </q-item>
      </div>
      <div class="col-6">
        <q-input v-model="contactPerson" outlined dense label="Name (email)"></q-input>
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <q-item class="q-mr-auto">
          <q-item-section>
            <q-item-label class="text-right">Frequency</q-item-label>
          </q-item-section>
        </q-item>
      </div>
      <div class="col-6">
        <q-select
          v-model="frequency"
          label="Frequency"
          :options="frequencyOptions"
          popup-content-class="alternative-id-popup"
          emit-value
          map-options
          dense
          outlined
          options-dense
          option-value="value"
          option-label="label"
        />
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <q-item class="q-mr-auto">
          <q-item-section>
            <q-item-label class="text-right">Invite Link</q-item-label>
          </q-item-section>
        </q-item>
      </div>
      <div class="col-6">
        <q-input v-model="inviteLink" outlined dense label="URL"></q-input>
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <q-item class="q-mr-auto">
          <q-item-section>
            <q-item-label class="text-right">WIKI Link</q-item-label>
          </q-item-section>
        </q-item>
      </div>
      <div class="col-6">
        <q-input v-model="wikiLink" outlined dense label="URL"></q-input>
      </div>
    </div>
    <q-separator class="q-my-md" />
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <span class="title" style="font-size: 20px">Details</span>
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <q-item class="q-mr-auto">
          <q-item-section>
            <q-item-label class="text-right">Exclusive Option</q-item-label>
          </q-item-section>
        </q-item>
      </div>
      <div class="col-6">
        <q-checkbox v-model="exclusiveForType" outlined label="Exclusive Option"></q-checkbox>
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <q-item class="q-mr-auto">
          <q-item-section>
            <q-item-label class="text-right">Cloud Platform</q-item-label>
          </q-item-section>
        </q-item>
      </div>
      <div class="col-6">
        <q-select
          v-model="cloudType"
          label="Cloud Type"
          :options="cloudTypesOptions"
          popup-content-class="alternative-id-popup"
          emit-value
          multiple
          clearable
          map-options
          dense
          outlined
          options-dense
        />
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <q-item class="q-mr-auto">
          <q-item-section>
            <q-item-label class="text-right">SLA Commitment</q-item-label>
          </q-item-section>
        </q-item>
      </div>
      <div class="col-6">
        <q-input v-model="slaCommitment" outlined dense label="SLA Commitment"></q-input>
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <q-item class="q-mr-auto">
          <q-item-section>
            <q-item-label class="text-right">Programming Languages</q-item-label>
          </q-item-section>
        </q-item>
      </div>
      <div class="col-6">
        <q-input v-model="programmingLanguage" outlined dense label="Programming Languages"></q-input>
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm">
      <div class="col-5">
        <q-item class="q-mr-auto">
          <q-item-section>
            <q-item-label class="text-right">Supported Regions</q-item-label>
          </q-item-section>
        </q-item>
      </div>
      <div class="col-6">
        <q-input v-model="supportedRegions" outlined dense label="Supported Regions"></q-input>
      </div>
    </div>
    <div>
      The remaining items in the details section are sourced from your ServiceNow Support Group. To make any changes,
      please update them directly in ServiceNow.
    </div>
    <div class="q-mt-lg"><q-checkbox v-model="pageSettings.details" label="Show Details section"></q-checkbox></div>
  </DialogForm>

  <DialogForm
    :show="showEditDescription"
    @close="closeEditDescription"
    @submit="updateInfraservice"
    :updLoading="updLoading"
    title="Edit description"
  >
    <div v-if="newDescription" class="row full-width">
      <q-input
        class="full-width"
        label="Title description"
        :rules="[(val) => !!val || 'Field is required', titleDescRule]"
        v-model="newTitleDescription"
        outlined
        dense
      />
    </div>
    <div class="row q-col-gutter-md">
      <div class="col-6">
        <q-scroll-area
          :style="{ height: widgetHeight }"
          :thumb-style="{ borderRadius: '5px', background: '#9f9f9f', width: '2px', opacity: 1, right: 0 }"
        >
          <q-input
            type="textarea"
            label="Text description"
            ref="inputDescription"
            :rules="[(val) => !!val || 'Field is required']"
            hint="Please, use markdown"
            v-model="description"
            outlined
          ></q-input>
        </q-scroll-area>
      </div>
      <div class="col-6">
        <q-scroll-area
          :style="{ height: widgetHeight }"
          :thumb-style="{ borderRadius: '5px', background: '#9f9f9f', width: '2px', opacity: 1, right: 0 }"
        >
          <q-resize-observer @resize="onResize"></q-resize-observer>
          <div class="single-page-markdown" v-html="dataMarkdown(description)"></div>
        </q-scroll-area>
      </div>
    </div>
  </DialogForm>
</template>

<script>
import store from '@/store';
import { mapGetters, mapActions } from 'vuex';
import gql from 'graphql-tag';
import { marked } from 'marked';
import emitter from 'tiny-emitter/instance';
import { scroll } from 'quasar';
import { v4 as uuidv4 } from 'uuid';
import apollo from '@/library/http/apollo';
import { getUserBatchPermissions } from '@/pages/digitalService/api';
import InfraServiceLogo from '@/shared/components/InfraServiceLogo.vue';
import { BANNERS_GETTERS } from '@/store/modules/banners';
import { SERVICE_DESIGN_ACTIONS, SERVICE_DESIGN_GETTERS } from '@/store/modules/service-design';
import { DIGITAL_SERVICES_GETTERS } from '@/store/modules/digital-services';
import { APP_GETTERS, APP_ACTIONS } from '@/store/modules/app';
import * as uiConfig from '@/shared/ui-config.json';
import { supportCntrlClick } from '@/shared/helpers';
import { preCreateDraft } from '@/pages/digitalService/api';
import TechnicalYellowPage from './components/TechnicalYellowPage.vue';
import FeedbackDialog from '@/shared/components/FeedbackDialog';
import DialogForm from './components/DialogForm.vue';
import { getEnums } from './api';
import { capitalizeFirstLetter } from '@/shared/helpers';

const { getScrollTarget, setVerticalScrollPosition } = scroll;

export default {
  name: 'InfraService',
  components: {
    InfraServiceLogo,
    TechnicalYellowPage,
    FeedbackDialog,
    DialogForm,
  },
  async mounted() {
    this.uiConfig = uiConfig[process.env.VUE_APP_ENV];
    this.$showLoading();
    const states = await getEnums('EServiceState');
    this.stateOptions = states.map((i) => ({ value: i.key, label: i.value }));
    const cloudTypes = await getEnums('ECloudType');
    this.cloudTypesOptions = cloudTypes.map((i) => ({ value: i.key, label: i.value }));
    const linkTypes = await getEnums('ELinkType');
    this.linkTypes = linkTypes.map((i) => ({ value: i.key, label: i.value }));
    const links = await getEnums('EInfraServiceLinkTitle');
    this.requiredLinks = links.map((i) => i.value);
    const descriptions = await getEnums('EInfraServiceDescriptionTitle');
    this.requiredDescriptions = descriptions.map((i) => i.value);
    const frequency = await getEnums('EOpenHouseFrequency');
    this.frequencyOptions = frequency.map((i) => ({ value: i.key, label: i.value }));
    try {
      await this.init();
    } finally {
      this.$hideLoading();
    }
  },
  computed: {
    ...mapGetters('digitalServices', {
      currentService: DIGITAL_SERVICES_GETTERS.CURRENT_SERVICE,
    }),
    ...mapGetters('serviceDesign', {
      design: SERVICE_DESIGN_GETTERS.ALL,
    }),
    ...mapGetters('banners', {
      currentBanner: BANNERS_GETTERS.CURRENT,
    }),
    ...mapGetters('app', {
      userInfo: APP_GETTERS.USER_INFO,
      infraServiceAuthSettings: APP_GETTERS.INFRA_SERVICE_AUTH_SETTINGS,
    }),
    titleDescRule() {
      return (val) =>
        !this.requiredDescriptions.includes(capitalizeFirstLetter(val)) || `${val} description already exist`;
    },
    emailRules() {
      const pattern = new RegExp('^\\S+@\\S+\\.\\S+$');
      return [(val) => !!val || 'Field is required', (val) => pattern.test(val) || 'Invalid email'];
    },
    urlRule() {
      const pattern = new RegExp(
        '^(https?:\\/\\/)?' + // protocol
          '([a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}|localhost|((\\d{1,3}\\.){3}\\d{1,3}))' + // domain or ip
          '(\\:\\d+)?' + // port
          '(\\/[^\\s]*)?$', // path, query, fragment
        'i',
      );
      return (val) => !val || pattern.test(val) || 'Invalid URL';
    },
    supportHoursColor() {
      const now = new Date();
      const day = now.getUTCDay(); // Get the current day of the week (0-6)
      const getCETOffset = new Date().getTimezoneOffset() === -60 ? 1 : 2;
      const hour = now.getUTCHours() + getCETOffset; // Get the current hour (0-23)
      const hours = this.apmDetails?.supportGroupDetails?.[0]?.workDayHours;
      if (!hours) return 'red-14'; // Fallback in case of missing data
      const [startTime, endTime] = hours.split(' - ').map((time) => parseInt(time.split(':')[0])); // Extract hours
      // Check if the current day is Monday to Friday (1-5) and the current hour is between 8 and 18 CET
      if (day >= 1 && day <= 5 && hour >= startTime && hour < endTime) {
        // Adjusted for UTC
        return 'light-green-13';
      } else {
        return 'red-14';
      }
    },
    frequencyLabel() {
      if (!this.dataService?.openHouseInfo?.frequency || !this.frequencyOptions.length) {
        return this.dataService?.openHouseInfo?.frequency || 'N/A';
      }
      const frequencyOption = this.frequencyOptions.find(
        (option) => option.value === this.dataService.openHouseInfo.frequency,
      );
      return frequencyOption ? frequencyOption.label : this.dataService.openHouseInfo.frequency;
    },
    details() {
      if (!this.dataService || !this.apmDetails) {
        return [];
      }
      const formatPercentage = (value) => {
        const num = parseFloat(value); // Convert to float if it's a string
        return !isNaN(num) ? (num * 100).toFixed(2) + '%' : 'N/A';
      };
      const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? 'N/A' : date.toISOString().split('T')[0]; // Extract YYYY-MM-DD
      };
      return [
        { label: 'Service Type', value: this.dataService?.categoryName, show: true },
        { label: 'Exclusive option', value: this.dataService?.exclusiveForType, show: true },
        {
          label: 'Cloud Platform',
          value: this.dataService?.cloudType.map((i) => capitalizeFirstLetter(i)).join(', ') || 'N/A',
          show: true,
        },
        { label: 'APM', value: this.dataService?.apmid || 'N/A', show: true },
        {
          label: 'Support group',
          value: this.apmDetails?.supportGroupDetails?.length
            ? this.apmDetails?.supportGroupDetails[0].groupName
            : 'N/A',
          show: true,
        },
        {
          label: 'Support Hours',
          value: this.apmDetails?.supportGroupDetails?.length
            ? this.apmDetails?.supportGroupDetails[0].workDayHours
            : 'N/A',
          avatar: true, // indicate if the avatar should be displayed
          show: true,
        },
        {
          label: 'On Call',
          value: this.apmDetails?.supportGroupDetails?.length ? this.apmDetails?.supportGroupDetails[0].onCall : 'N/A',
          show: true,
        },
        {
          label: 'On Call Number',
          value: this.apmDetails?.supportGroupDetails?.length
            ? this.apmDetails?.supportGroupDetails[0].onCallNumber
            : 'N/A',
          show:
            (this.apmDetails?.supportGroupDetails?.length && this.apmDetails?.supportGroupDetails[0].onCall) !== false,
        },
        {
          label: 'MI Availability (12m)',
          value: formatPercentage(this.apmDetails?.supportData?.availabilityPercentage),
          show: true,
        },
        { label: 'SLA Commitment', value: formatPercentage(this.dataService?.slaCommitment), show: true },
        { label: 'Department', value: this.apmDetails?.supportData?.department || 'N/A', show: true },
        { label: 'Linked service offering', value: this.apmDetails?.supportData?.serviceOffering || 'N/A', show: true },
        { label: 'Programming languages', value: this.dataService?.programmingLanguage || 'N/A', show: true },
        { label: 'Supported regions', value: this.dataService?.supportedRegions || 'N/A', show: true },
        { label: 'Last update', value: formatDate(this.dataService?.updatedAt), show: true },
      ];
    },
    dsPages() {
      return Math.ceil(this.filteredApms.length / this.showDsCount);
    },
    filteredApms() {
      if (!this.onboardedAPMs || !this.onboardedAPMs.length) {
        return [];
      }

      return this.onboardedAPMs.filter((apm) => {
        // Filter by APMID
        if (
          this.onboardedAPMsFilters.apmId &&
          !apm.apmId.toLowerCase().includes(this.onboardedAPMsFilters.apmId.toLowerCase())
        ) {
          return false;
        }

        // Filter by Name
        if (
          this.onboardedAPMsFilters.name &&
          !apm.name.toLowerCase().includes(this.onboardedAPMsFilters.name.toLowerCase())
        ) {
          return false;
        }

        // Filter by Business Domain
        if (
          this.onboardedAPMsFilters.businessDomain &&
          apm.businessDomain !== this.onboardedAPMsFilters.businessDomain
        ) {
          return false;
        }

        return true;
      });
    },
    showingDs() {
      const startIndex = (this.dsPage - 1) * this.showDsCount;
      const endIndex = startIndex + this.showDsCount;
      return this.filteredApms.slice(startIndex, endIndex);
    },
    canBeEdited() {
      return (
        this.userInfo.applicationsInOwn.find((app) => app.apmId === this.apmDetails.apmId) ||
        this.infraServiceAuthSettings.isEditGroup ||
        this.dataService.canBeEditable
      );
    },
    lifecycleBgClass() {
      if (this.dataService.state == 'default') {
        return 'bg-positive';
      } else if (this.dataService.state == 'planned') {
        return 'bg-warning';
      } else {
        return 'bg-negative';
      }
    },
    notCanBeDeleted() {
      const category = this.design.find((category) => category.categoryId === this.dataService.categoryId);
      return category
        ? category.infraServices.some((infra) => infra.apmid === this.dataService.apmid && infra.notCanBeDeleted)
        : false;
    },
    isInDesign() {
      const category = this.design.find((category) => category.categoryId === this.dataService.categoryId);
      return category ? category.infraServices.some((infra) => infra.apmid === this.dataService.apmid) : false;
    },
  },
  data() {
    return {
      showEditAdrs: false,
      showEditTyp: false,
      showEditOnboardApms: false,
      updLoading: false,
      businessDomains: [],
      businessDomainOptions: [],
      onboardedAPMs: [],
      onboardedAPMsFilters: {
        apmId: undefined,
        name: undefined,
        businessDomain: undefined,
      },
      pageSettings: {},
      requiredDescriptions: [],
      requiredLinks: [],
      dataService: undefined,
      addserviceLoad: false,
      addServiceError: undefined,
      dsPage: 1,
      showDsCount: 8,
      loadFeedback: false,
      feedbackDialog: false,
      showEditLifecycle: false,
      lifecycleStage: null,
      contacts: [],
      showEditContacts: false,
      metadata: [],
      showEditMetadata: false,
      links: [],
      showEditLinks: false,
      showEditName: false,
      displayName: null,
      subtitle: null,
      showEditDescription: false,
      description: null,
      descriptionId: undefined,
      newTitleDescription: undefined,
      contactPerson: undefined,
      frequency: undefined,
      frequencyOptions: [],
      inviteLink: undefined,
      wikiLink: undefined,
      widgetHeight: '200px',
      stateOptions: [],
      cloudTypesOptions: [],
      linkTypes: [],
      showEditDetails: false,
      cloudType: [],
      exclusiveForType: false,
      slaCommitment: null,
      programmingLanguage: null,
      supportedRegions: null,
      newDescription: false,
      apmDetails: {
        id: 'N/A',
        apmId: 'N/A',
        majorIncidents: 'N/A',
        assignmentGroup: 'N/A',
        businessOwnerEmail: 'N/A',
        businessOwnerId: 'N/A',
        businessOwnerName: 'N/A',
        createdAt: 'N/A',
        createdByEmail: 'N/A',
        createdById: 'N/A',
        createdByName: 'N/A',
        digitalServiceName: 'N/A',
        status: 'N/A',
        digitalServiceActive: 'N/A',
        itCustodian: 'N/A',
        itCustodianEmail: 'N/A',
        itCustodianId: 'N/A',
        itCustodianName: 'N/A',
        itOwner: 'N/A',
        itOwnerEmail: 'N/A',
        itOwnerId: 'N/A',
        businessOwnerCustodianId: 'N/A',
        businessOwnerCustodianName: 'N/A',
        businessOwnerCustodianEmail: 'N/A',
        itOwnerName: 'N/A',
        release: 'N/A',
        url: 'N/A',
        supportGroupDetails: [],
      },
    };
  },
  methods: {
    ...mapActions('app', {
      setPageTitle: APP_ACTIONS.SET_PAGE_TITLE,
      setEditGroup: APP_ACTIONS.SET_INFRA_SERVICE_EDIT_GROUP,
    }),
    ...mapActions('serviceDesign', {
      setInfraService: SERVICE_DESIGN_ACTIONS.SET_INFRASERVICE,
      removeInfraService: SERVICE_DESIGN_ACTIONS.REMOVE_INFRASERVICE,
    }),
    closeEditDialog() {
      this.showEditName = false;
      this.showEditOnboardApms = false;
      this.showEditTyp = false;
      this.showEditAdrs = false;
      this.showEditContacts = false;
      this.showEditMetadata = false;
      this.showEditDetails = false;
      this.pageSettings = JSON.parse(JSON.stringify(this.dataService?.pageSettings || {}));
    },
    filterFn(val, update) {
      if (val === '') {
        update(() => {
          this.businessDomainOptions = this.businessDomains;
        });
        return;
      }
      update(() => {
        const needle = val.toLowerCase();
        this.businessDomainOptions = this.businessDomains.filter((v) => v.toLowerCase().indexOf(needle) > -1);
      });
    },
    removeDescription(id) {
      const index = this.dataService.descriptions.findIndex((item) => item.id === id);
      this.dataService.descriptions.splice(index, 1);
      this.updateInfraservice();
    },
    async updateInfraservice() {
      this.updLoading = true;
      if (this.descriptionId && this.description.length) {
        const updDescription = this.dataService.descriptions.find((i) => i.id === this.descriptionId);
        if (updDescription) {
          updDescription.text = this.description;
        } else {
          this.dataService.descriptions.push({
            id: this.descriptionId,
            type: this.newTitleDescription,
            text: this.description,
          });
        }
      }

      const updateData = {
        contacts: this.showEditContacts ? this.contacts : this.dataService.contacts,
        metadata: this.showEditMetadata
          ? this.metadata.reduce((acc, entry) => {
              if (entry.label && entry.value) acc[entry.label] = entry.value;
              return acc;
            }, {})
          : this.dataService.metadata || null,
        links: this.links.filter((link) => link.url?.length),
        descriptions: this.dataService.descriptions
          .map((i) => {
            return {
              id: i.id,
              type: i.type,
              text: i.text,
              version: i.version,
            };
          })
          .filter((desc) => desc.text?.length),
        displayName: this.displayName?.length ? this.displayName : this.dataService.displayName,
        subtitle: this.subtitle?.length ? this.subtitle : this.dataService.subtitle,
        infraServiceId: this.$route.params.id,
        cloudType: this.cloudType?.length ? this.cloudType : this.dataService.cloudType,
        serviceType: this.serviceType?.length ? this.serviceType : this.dataService.serviceType,
        lifecycleStage: this.lifecycleStage?.length ? this.lifecycleStage : this.dataService.lifecycleStage,
        exclusiveForType: this.exclusiveForType,
        slaCommitment: this.slaCommitment ? parseFloat(this.slaCommitment) : parseFloat(this.dataService.slaCommitment),
        programmingLanguage: this.programmingLanguage?.length
          ? this.programmingLanguage
          : this.dataService.programmingLanguage,
        supportedRegions: this.supportedRegions?.length ? this.supportedRegions : this.dataService.supportedRegions,
        openHouseInfo: {
          contactPerson: !this.contactPerson?.length
            ? 'N/A'
            : this.contactPerson?.length
            ? this.contactPerson
            : this.dataService.openHouseInfo.contactPerson,
          frequency: this.frequency?.length ? this.frequency : this.dataService.openHouseInfo.frequency,
          wikiLink: !this.wikiLink?.length
            ? 'N/A'
            : this.wikiLink?.length
            ? this.wikiLink
            : this.dataService.openHouseInfo.wikiLink,
          inviteLink: !this.inviteLink?.length
            ? 'N/A'
            : this.inviteLink?.length
            ? this.inviteLink
            : this.dataService.openHouseInfo.inviteLink,
        },
        pageSettings: this.pageSettings,
      };

      await apollo.apolloClient
        .mutate({
          mutation: gql`
            mutation updateInfraService($data: FrontendInfraserviceInput!) {
              updateInfraService(data: $data)
            }
          `,
          variables: {
            data: updateData,
          },
        })
        .then(async () => {
          this.$notify('positive', 'Success', `Infraservice was updated successfully.`);

          if (updateData.displayName) {
            await store.dispatch('app/setPageTitle', updateData.displayName);
          }
        })
        .catch((error) => {
          this.$notify('negative', 'Error', error.message);
        })
        .finally(async () => {
          await this.init();
          this.reset();
          this.closeEditDialog();
          this.updLoading = false;
        });
    },
    closeEditDescription() {
      this.descriptionId = undefined;
      this.description = null;
      this.showEditDescription = false;
      this.newDescription = false;
      this.newTitleDescription = null;
    },
    reset() {
      this.closeEditDescription();
      this.showEditName = false;
      this.showEditLinks = false;
      this.showEditDetails = false;
      this.showEditContacts = false;
      this.showEditMetadata = false;
      this.showEditLifecycle = false;
    },
    onResize(size) {
      if (size.height > 600) {
        this.widgetHeight = '600px';
        this.$refs.inputDescription.$el.control.style.height = `560px`;
      }
    },
    showEditDescriptionDialog(id) {
      this.showEditDescription = true;
      this.descriptionId = id;
      this.description = this.dataService.descriptions.find((i) => i.id === id).text || null;
    },
    showNewDescriptionDialog() {
      this.showEditDescription = true;
      this.newDescription = true;
      this.descriptionId = uuidv4();
      this.description = null;
    },
    showEditDetailsDialog() {
      this.showEditDetails = true;
      this.exclusiveForType = this.dataService.exclusiveForType;
      this.cloudType = this.dataService.cloudType;
      this.slaCommitment = this.dataService.slaCommitment;
      this.programmingLanguage = this.dataService.programmingLanguage;
      this.supportedRegions = this.dataService.supportedRegions;
      this.contactPerson = this.dataService.openHouseInfo.contactPerson;
      this.frequency = this.dataService.openHouseInfo.frequency;
      this.inviteLink = this.dataService.openHouseInfo.inviteLink;
      this.wikiLink = this.dataService.openHouseInfo.wikiLink;
    },
    showEditDisplayNameDialog() {
      this.showEditName = true;
      this.displayName = this.dataService.displayName;
      this.subtitle = this.dataService.subtitle;
    },
    showEditContactsDialog() {
      this.showEditContacts = true;
      this.contacts = this.dataService.contacts ? JSON.parse(JSON.stringify(this.dataService.contacts)) : [];
    },
    showEditMetadataDialog() {
      this.showEditMetadata = true;
      const meta = this.dataService.metadata || {};
      this.metadata = Object.entries(meta).map(([label, value]) => ({ label, value }));
    },
    addMetadata() {
      this.metadata.push({ label: '', value: '' });
    },
    removeMetadata(index) {
      this.metadata.splice(index, 1);
    },
    closeEditLinksDialog() {
      this.showEditLinks = false;
      this.links = JSON.parse(JSON.stringify(this.dataService.links));
    },
    showEditLinksDialog() {
      this.showEditLinks = true;
      this.links = JSON.parse(JSON.stringify(this.dataService.links));
    },
    addContact() {
      this.contacts.push({ label: '', contact: '' });
    },
    removeContact(index) {
      this.contacts.splice(index, 1);
    },
    addLink() {
      this.links.push({ label: '', url: '', type: undefined, description: '', id: uuidv4() });
    },
    removeLink(index) {
      this.links.splice(index, 1);
    },
    showEditLifecycleDialog() {
      this.showEditLifecycle = true;
      this.lifecycleStage = this.dataService.state;
    },
    async createFeedback(feedback) {
      this.loadFeedback = true;
      await apollo.apolloClient
        .mutate({
          mutation: gql`
            mutation createFeedback($infraServiceId: String, $feedback: FrontendFeedbackInput!) {
              createFeedback(feedback: $feedback, infraServiceId: $infraServiceId)
            }
          `,
          variables: {
            feedback: feedback,
            infraServiceId: this.dataService.serviceId,
          },
        })
        .then((res) => {
          if (res.data.createFeedback) {
            this.$notify('positive', 'Success', `Feedback for ${this.dataService.displayName} was sent successfully.`);
          }
        })
        .catch((error) => {
          this.$notify('negative', 'Error', error.message);
        })
        .finally(async () => {
          this.loadFeedback = false;
          this.feedbackDialog = false;
          await this.init();
        });
    },
    showFeedbackDialog() {
      if (this.dataService.type === 'Certified Product') {
        window.open(
          'https://swissreesm.service-now.com/contactone?id=sc_cat_item&table=sc_cat_item&sys_id=674a6d331be7319053e740c1b24bcb1c',
          '_blank',
        );
      } else {
        this.feedbackDialog = true;
      }
    },
    scrollToItem(anchor) {
      const element = this.$refs[anchor];
      const target = getScrollTarget(element);
      const offset = element.offsetTop - (this.currentBanner ? 95 : 45);
      const duration = 1000;
      setVerticalScrollPosition(target, offset, duration);
    },
    downloadCsv() {
      const name = this.dataService.displayName || this.dataService.serviceName;
      const apmId = this.dataService.apmid;
      const registrations = this.dataService.registrations.map((reg) => ({
        digitalServiceName: reg.digitalService.digitalServiceName,
        apmId: reg.digitalService.apmId,
      }));
      const currentUrl = window.location.origin;
      const csvRows = [];
      csvRows.push('Shared Service Name,Shared Service APM ID,Digital Service Name,Digital Service APM ID,Link');
      registrations.forEach((reg) => {
        const link = `${currentUrl}/my-portfolio/${reg.apmId}`;
        csvRows.push(`${name},${apmId},${reg.digitalServiceName},${reg.apmId},${link}`);
      });
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${apmId}-registrations.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    async addToServiceDesign() {
      if (this.isInDesign) {
        this.removeInfraService({
          category: { categoryId: this.dataService.categoryId },
          infraService: this.dataService,
        });
        this.$notify(
          'info',
          'Info',
          `${
            this.dataService.displayName || this.dataService.serviceName
          } was removed from your service design. Please choose digital service if you would like to onboard onto it.`,
        );
      } else {
        this.addserviceLoad = true;
        try {
          const data = {
            infraServiceApmId: this.dataService.apmid,
            digitalServiceApmId: this.currentService.apmId,
          };
          await preCreateDraft(data);
          this.setInfraService({
            category: { categoryId: this.dataService.categoryId },
            infraService: this.dataService,
          });
          this.$notify(
            'info',
            'Info',
            `${
              this.dataService.displayName || this.dataService.serviceName
            } was added to your service design. Please choose digital service if you would like to onboard onto it.`,
          );
        } catch (error) {
          this.addServiceError = error.message;
          this.$notify('negative', 'Error', error.message);
        } finally {
          this.addserviceLoad = false;
        }
      }
    },
    goToAdr(event, id) {
      supportCntrlClick(event, `/adrs/${id}`);
    },
    async checkPermissions(apmid) {
      const authData = {
        userEmail: this.userInfo.email,
        resources: [
          {
            scope: {
              platformId: apmid,
            },
            resource: {
              type: 'management',
              name: 'service.catalog.manage',
            },
          },
        ],
      };
      const res = await getUserBatchPermissions(authData);
      const isEditGroup = res[apmid]['service.catalog.manage'];
      await this.setEditGroup(isEditGroup);
    },
    dataMarkdown(data) {
      return data ? marked(data) : '';
    },
    ensureProtocol(url) {
      if (!url) return url;
      // If URL already has a protocol, return as is
      if (/^https?:\/\//i.test(url)) {
        return url;
      }
      // If URL starts with //, add https:
      if (url.startsWith('//')) {
        return `https:${url}`;
      }
      // If URL looks like a domain (contains a dot but no slash at start), add https://
      if (url.includes('.') && !url.startsWith('/')) {
        return `https://${url}`;
      }
      // Otherwise, return as is (could be a relative path)
      return url;
    },
    async init() {
      const { id } = this.$route.params;
      try {
        await apollo.apolloClient
          .query({
            query: gql`
                query {
                  infraServiceById(id: "${id}") {
                    id
                    apmid
                    categoryId
                    categoryName
                    serviceId
                    orderable
                    icon
                    cloudType
                    description
                    createdAt
                    displayName
                    subtitle
                    parentServiceDisplayName
                    pageSettings
                    openHouseInfo {
                      contactPerson
                      frequency
                      inviteLink
                      wikiLink
                    }
                    registrations {
                      digitalService {
                        apmId
                        digitalServiceName
                        businessDomain
                      }
                    }
                    standards {
                      id
                      standardName
                      standardLink
                    }
                    templates {
                      name
                      createdAt
                    }
                    pavedRoad
                    contacts {
                      label
                      contact
                    }
                    metadata
                    serviceName
                    state
                    type
                    links {
                      id
                      label
                      description
                      type
                      url
                    }
                    dependencies {
                      id
                      contacts {
                        label
                        contact
                      }
                      apmid
                      categoryId
                      description
                      displayName
                      pavedRoad
                      serviceName
                      state
                      type
                    }
                    adrs {
                      adrId
                      id
                      name
                      scope
                      status
                      validDate
                      version
                      content
                      requirements
                    }
                    feedbacks {
                      id
                      title
                      description
                      isLike
                      stars
                      createdAt
                      createdBy {
                        name
                      }
                    }
                    descriptions {
                      createdAt
                      deletedAt
                      id
                      text
                      type
                      updatedAt
                      version
                    }
                    exclusiveForType
                    programmingLanguage
                    slaCommitment
                    supportedRegions
                    updatedAt
                  }
                }
              `,
          })
          .then(async (response) => {
            this.dataService = response.data.infraServiceById;
            this.pageSettings = this.dataService.pageSettings
              ? JSON.parse(JSON.stringify(this.dataService.pageSettings))
              : {};
            this.onboardedAPMs = this.dataService.registrations.map((reg) => ({
              apmId: reg.digitalService.apmId,
              businessDomain: reg.digitalService.businessDomain?.name,
              name: reg.digitalService.digitalServiceName,
            }));
            // Extract unique business domains from the current service's registrations
            const currentServiceDomains = [
              ...new Set(
                this.dataService.registrations
                  .map((reg) => reg.digitalService.businessDomain?.name)
                  .filter((domain) => domain),
              ),
            ];

            // Merge with existing business domains if they exist, otherwise use current service domains
            this.businessDomains =
              this.businessDomains.length > 0
                ? [...new Set([...this.businessDomains, ...currentServiceDomains])]
                : currentServiceDomains;
            this.businessDomainOptions = this.businessDomains;
            await this.checkPermissions(this.dataService.apmid);
            this.requiredDescriptions.forEach((desc) => {
              if (!this.dataService.descriptions.find((i) => i.type === desc)) {
                this.dataService.descriptions.push({ id: uuidv4(), type: desc, text: null });
              }
            });
            this.requiredLinks.forEach((link) => {
              if (!this.dataService.links.find((i) => i.label === link)) {
                this.dataService.links.push({ id: uuidv4(), label: link, url: null });
              }
            });
          });

        await apollo.apolloClient
          .query({
            query: gql`
            query {
                getApplicationByAPMID(digitalServiceAPMID: "${this.dataService.apmid}") {
                  id
                  apmId
                  majorIncidents
                  assignmentGroup
                  businessOwnerEmail
                  businessOwnerId
                  businessOwnerName
                  createdAt
                  createdByEmail
                  createdById
                  createdByName
                  digitalServiceName
                  status
                  digitalServiceActive
                  itCustodian
                  itCustodianEmail
                  itCustodianId
                  itCustodianName
                  itOwner
                  itOwnerEmail
                  itOwnerId
                  businessOwnerCustodianId
                  businessOwnerCustodianName
                  businessOwnerCustodianEmail
                  itOwnerName
                  release
                  url
                  supportData {
                    availabilityPercentage
                    department
                    serviceOffering
                  }
                  supportGroupDetails {
                    groupEmail
                    groupId
                    groupName
                    members
                    membersEmails
                    onCall
                    onCallNumber
                    teamsChannel
                    timezone
                    weekendHours
                    workDayHours
                  }
                }
              }
            `,
          })
          .then((response) => {
            this.apmDetails = response.data.getApplicationByAPMID;
          });
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      }
    },
  },
  created() {
    emitter.on('showEditInfraServiceName', () => {
      this.showEditDisplayNameDialog();
    });
  },
};
</script>

<style lang="scss" scoped>
.descr-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.icon-service {
  display: inline-block;
  padding: 10px;
  border-radius: 50%;
  background-color: var(--q-primary);
}
.section-titles {
  list-style-type: none;
  font-size: 20px;
  font-weight: bold;

  li {
    margin-bottom: 5px;
    cursor: pointer;
  }
}
.service-section {
  border-radius: 15px;
}
h5 {
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 0;
}
.links {
  flex-wrap: wrap;
  list-style-type: none;
  margin: 0 -5px;

  li {
    width: 100%;
    margin: 0 5px !important;
  }

  a {
    display: inline-block;
    text-decoration: none;
    color: $text-color;
  }
}

.open-house {
  list-style-type: none;
  margin-top: 5px;
}

.list {
  list-style-type: none;
  -moz-column-count: 2; /* Firefox */
  -webkit-column-count: 2; /* Safari and Chrome */
  column-count: 2;
}
.contact-label {
  display: block;
  color: var(--q-primary);
  font-weight: bold;
}

.dependencies {
  a {
    color: $text-color;
  }
}

.icon-service {
  &.private {
    background-color: #428ce1;
  }
  &.public {
    background-color: #73e1a5;
  }
  &.onprem {
    background-color: #a88cd1;
  }
  &.na {
    background-color: #b3bb7e;
  }
  &.tool {
    background-color: #bfb9a7;
  }
}

.adr {
  height: 200px;
  border: 1px solid $grey-5;
  border-radius: 10px;
  padding: 10px;
  cursor: pointer;

  .adr-title {
    display: block;
    font-size: 18px;
    font-weight: bold;
    white-space: nowrap;
    max-width: calc(100% - 5px);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .scope {
    display: block;
    white-space: nowrap;
    max-width: calc(100% - 5px);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .adr-content {
    height: 105px;
    overflow: hidden;
    font-size: 12px;
  }

  .metadata {
    white-space: nowrap;
    max-width: calc(100% - 5px);
    overflow: hidden;
    font-weight: bold;
    font-size: 12px;

    span {
      margin-right: 5px;
    }
  }
}
.single-column .detail-item {
  width: 100%;
}

.two-columns {
  display: flex;
  flex-wrap: wrap;
}

.two-columns .detail-item {
  width: 50%;
}
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
.custom-avatar {
  width: 10px;
  height: 10px;
  font-size: 0; /* Hide any text inside the avatar */
  animation: pulse 1.5s infinite;
}
.float-right {
  margin-left: auto;
}
.apm-link {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  line-height: 1.2;
  sup {
    color: $grey-7;
  }
}
</style>
